/* tslint:disable: max-line-length no-console */

import { HttpErrorResponse } from "@angular/common/http";
import { ErrorHandler, Inject, Injectable, Optional } from "@angular/core";
import { CustomError } from "./custom-error.service";
import {
  IBreadcrumb,
  IContextDetails,
  IErrorHandlerOptions,
  IErrorOptions,
  IHint,
  ILoggerService,
  INotificationService,
  OfLoggerService,
  OfNotificationService,
} from "./error-handler-interfaces.service";
import {
  AuthErrorStrategy,
  ConnectionErrorStrategy,
  GenericErrorStrategy,
  HttpErrorStrategy,
  ShowNotificationStrategy,
} from "./error-strategies.service";

@Injectable({ providedIn: "root" })
export class ErrorHandlerService implements ErrorHandler {
  errorId: string;
  private notificationStyle =
    "padding: 1rem; background: #bbdefb; border: 2px solid #8aacc8; font-size: .9rem; line-height: 3rem";
  private contextStyle =
    "padding: 1rem; background: #ffffa6; border: 2px solid #dce775; font-size: .9rem; line-height: 3rem";
  private logStyle =
    "padding: 1rem; background: #efdcd5; border: 2px solid #bcaaa4; font-size: .9rem; line-height: 3rem";
  private understateStyle = "color: #666; background: #fff; font-size: .7rem;";

  public logger: ILoggerService;
  public notifications: INotificationService;
  private _options: IErrorHandlerOptions;

  constructor(
    @Inject("SHOW_ERROR_DETAILS")
    @Optional()
    private _showErrorDetails: boolean,
    @Inject("LOGGER_SERVICE") @Optional() private _logger: OfLoggerService,
    @Inject("NOTIFICATION_SERVICE")
    @Optional()
    private _notifications: OfNotificationService,
    @Inject("LOGIN_URL") @Optional() private _authRedirectUrl: string,
    @Inject("ERROR_HANDLER_OPTIONS")
    @Optional()
    private _userOptions: IErrorHandlerOptions
  ) {
    // Initialize the logger with injected service or fallback implementation
    this.logger =
      this._logger ||
      ({
        logError: (err, details = {}) => {
          console.log(
            "%c📜 LOG ERROR:",
            this.logStyle,
            err,
            "\nDETAILS:\n " + JSON.stringify(details)
          );
        },
        logMessage: (message) => {
          console.log(
            `%c📜 LOG MESSAGE: ${message}`,
            this.logStyle,
            "\n",
            message
          );
        },
        logBreadcrumb: (details: IBreadcrumb, hint: IHint) => {
          console.log(
            "%c📜 LOG BREADCRUMB:",
            this.understateStyle,
            "\n",
            details,
            hint
          );
        },
        logEvent: (event) => {
          console.log("%c📜 LOG EVENT:", this.logStyle, "\n", event);
        },
        configureContext: (context: IContextDetails) => {
          console.log("%c🗳️ UPDATE CONTEXT", this.contextStyle, "\n", context);
        },
      } as ILoggerService);

    // Initialize the notifications with injected service or fallback implementation
    this.notifications =
      this._notifications ||
      ({
        show: (message = "") => {
          console.info(
            `%c🔔 ERROR NOTIFICATION: ${message}`,
            "padding: 1rem; background: #bbdefb; border: 2px solid #8aacc8; font-size: .9rem;"
          );
        },
        showError: (message = "") => {
          console.info(
            `️%c🔔 ERROR NOTIFICATION: Sorry, there\'s been an error! ${message}`,
            this.notificationStyle
          );
        },
        notify: (message = "", priority = 0, options = { viewOptions: {} }) => {
          let nextSteps = [];
          if (options.viewOptions && options.viewOptions.controls) {
            nextSteps = options.viewOptions.controls.map(
              (c) => `> ${c.text || "OK"}`
            );
          }
          console.info(
            `️%c⚠️ ERROR NOTIFICATION: Sorry, there\'s been an error! ${message}`,
            this.notificationStyle
          );
        },
      } as INotificationService);

    // Initialize _options
    this._options = {
      authRedirectUrl:
        this._authRedirectUrl || "https://login.orthofi.com/sign-in",
      consoleLogErrors: this._showErrorDetails || false,
      ...this._userOptions,
    };
  }

  private getLoginUrlWithRedirect = (
    base = this._options.authRedirectUrl,
    redirect = window.location.href
  ) => `${base}/?redirect_url=${encodeURIComponent(redirect)}`;

  handleError = (
    error: CustomError | HttpErrorResponse | Error | any,
    message?: string,
    providedSupportId?: string
  ): void => {
    if (this._options.consoleLogErrors) {
      console.error("Handled Error:", error);
    }

    let errorStrategy = new GenericErrorStrategy(
      this.notifications.notify,
      this.logger.logError,
      this.getLoginUrlWithRedirect(),
      message,
      providedSupportId
    );

    if (error instanceof HttpErrorResponse) {
      if (!navigator.onLine) {
        errorStrategy = new ConnectionErrorStrategy(
          this.notifications.notify,
          this.logger.logError,
          this.getLoginUrlWithRedirect(),
          undefined,
          providedSupportId
        );
      } else {
        switch (error.status) {
          case 401:
            errorStrategy = new AuthErrorStrategy(
              this.notifications.notify,
              this.logger.logError,
              this.getLoginUrlWithRedirect()
            );
            break;
          default:
            errorStrategy = new HttpErrorStrategy(
              this.notifications.notify,
              this.logger.logError,
              this.getLoginUrlWithRedirect(),
              message,
              providedSupportId
            );
            break;
        }
      }
    }

    errorStrategy.execute(error);
  };

  getErrorFunctions = (
    errorType: string,
    config: IErrorHandlerOptions = this._options
  ): IErrorOptions => {
    if (config && config.errorOptions && config.errorOptions[errorType]) {
      const options: IErrorOptions = config.errorOptions[errorType];
      return {
        displayFn:
          options.display !== false
            ? options.displayFn || this.notifications.notify
            : (message) => {},
        loggerFn:
          options.log !== false
            ? options.loggerFn || this.logger.logError
            : (error, errorDetails?) => {},
      };
    }
    return {
      displayFn: this.notifications.notify,
      loggerFn: this.logger.logError,
    };
  };

  invoke(
    invokedError: CustomError | any,
    customMessage?: string,
    customSupportId?: string
  ): void {
    this.handleError(invokedError, customMessage, customSupportId);
  }

  show(message?): void {
    new ShowNotificationStrategy(
      this.notifications.notify,
      this.logger.logError,
      this.getLoginUrlWithRedirect(),
      message
    ).execute();
  }

  showAndLog(error: CustomError, details = {}, message?: string): void {
    new GenericErrorStrategy(
      this.notifications.notify,
      this.logger.logError,
      this.getLoginUrlWithRedirect()
    ).invokeError(error, details, message);
  }
}
